import { Link } from "react-router";
import { TableCellLink } from "features/datagrid-configs/cellRenderers";
import type { SimpleColumn } from "ui-kit/src/Datagrid/DatagridV2";
import { formatApiStatus, formatDateTime, formatDuration, formatSource, getProviderMetadataField, getStatusClass, roundSizeString } from "../functions";
import { VideoMetadataFilterField, VideoProviderMetadataField, VideoProviderMetadataFilterField, type VideoTable } from "../metadata/types";
import { RowActionLink, rowActionIcons } from "./rowActions";

export type SortConfig = {
    name: string | undefined;
    sort: "asc" | "desc" | undefined;
};

export type Preset =
    | "waiting-for-approval"
    | "approved"
    | "in-processing"
    | "errored"
    | "finished"
    | "deleted"
    | "";

/** Floors only: a `width` would switch DatagridV2 to a fixed layout. */
export const stickyMinWidths = { select: 44, worker: 110 } as const;

function makeSortableHeader(
    columnName: string,
    sortConfig: SortConfig,
    setSortConfig: (val: SortConfig) => void,
    label?: string
) {
    const toggleSort = () => {
        let newOrder: "asc" | "desc" | undefined = "asc";
        if (sortConfig.name === columnName) {
            if (sortConfig.sort === "asc") {
                newOrder = "desc";
            } else if (sortConfig.sort === "desc") {
                newOrder = undefined;
            }
        }
        setSortConfig({ name: newOrder ? columnName : undefined, sort: newOrder });
    };

    return (
        <div onClick={toggleSort} className="tableHeader" >
            <div>{label}</div>
            <div>
                {sortConfig.name === columnName && sortConfig.sort
                    ? sortConfig.sort === "asc"
                        ? " ↑"
                        : " ↓"
                    : "↑↓"}
            </div>
        </div>
    );
}

export function workerCol(
	sortConfig: SortConfig,
	setSortConfig: (val: SortConfig) => void
): SimpleColumn<VideoTable> {
	return {
		id: "workerId",
		key: "workerId",
		label: "Worker",
		header: () => makeSortableHeader("worker", sortConfig, setSortConfig, "Worker"),
		cell: (value, row) =>
			row.node || row.workerId ? `${row.node}:${row.workerId}` : "-",
		minWidth: stickyMinWidths.worker,
		sticky: true,
	};
}

export function nameCol(
	sortConfig: SortConfig,
	setSortConfig: (val: SortConfig) => void
): SimpleColumn<VideoTable> {
	return {
		id: "name",
		key: "name",
		label: "Name",
		header: () => makeSortableHeader("name", sortConfig, setSortConfig, "Name"),
		cell: (value, row) => (
			<TableCellLink to={`/video-uploads/${row.id}/video`}>
				<span className="nameCell" title={row.name ?? undefined}>{row.name ?? "-"}</span>
			</TableCellLink>
		),
		sticky: true,
	};
}

export function createdAtCol(
    sortConfig: SortConfig,
    setSortConfig: (val: SortConfig) => void
): SimpleColumn<VideoTable> {
	return {
		id: "createdAt",
		key: "createdAt",
		label: "Uploaded",
		header: () => makeSortableHeader("createdAt", sortConfig, setSortConfig, "Uploaded"),
        cell: (value, row) => <div className="dateTimeCell">{formatDateTime(row.createdAt) ?? "-"}</div>,
    };
}

export function durationCol(
    sortConfig: SortConfig,
    setSortConfig: (val: SortConfig) => void
): SimpleColumn<VideoTable> {
    return {
		id: "duration",
		key: "duration",
		label: "Duration",
		header: () => makeSortableHeader("duration", sortConfig, setSortConfig, "Duration"),
        cell: (value, row) => <div className="dateTimeCell">{formatDuration(row.duration) ?? "-"}</div>,
    };
}

export function sizeCol(
    sortConfig: SortConfig,
    setSortConfig: (val: SortConfig) => void
): SimpleColumn<VideoTable> {
    return {
		id: "size",
		key: "size",
		label: "File Size",
		header: () => makeSortableHeader("size", sortConfig, setSortConfig, "File Size"),
        cell: (value, row) => <div className="dateTimeCell">{roundSizeString(row.size) ?? "-"}</div>,
    };
}

export function metadataCol(
    sortConfig: SortConfig,
    setSortConfig: (val: SortConfig) => void
): SimpleColumn<VideoTable> {
    return {
		id: "metadata",
		key: "metadata",
		label: "Game Id",
		header: () => makeSortableHeader(VideoMetadataFilterField.GameId, sortConfig, setSortConfig, "Game Id"),
        cell: (value, row) =>
            row.metadata && JSON.parse(row.metadata).gameId ?
                <Link target="_blank" to={`/admin/videos/match/${JSON.parse(row.metadata).gameId}`} className="dateTimeCell">
                    {row.metadata ? (JSON.parse(row.metadata).gameId ?? "-") : "-"}
                </Link>
                :
                <div className="dateTimeCell">-</div>,
    };
}

export function processingStartedCol(
    sortConfig: SortConfig,
    setSortConfig: (val: SortConfig) => void
): SimpleColumn<VideoTable> {
    return {
		id: "processingStartedAt",
		key: "processingStartedAt",
		label: "Processing Started",
		header: () => makeSortableHeader("processingStartedAt", sortConfig, setSortConfig, "Processing Started"),
        cell: (value, row) => <div className="dateTimeCell">{formatDateTime(row.processingStartedAt) ?? "-"}</div>,
    };
}

export function processingFinishedCol(
    sortConfig: SortConfig,
    setSortConfig: (val: SortConfig) => void
): SimpleColumn<VideoTable> {
    return {
		id: "processingFinishedAt",
		key: "processingFinishedAt",
		label: "Processing Finished",
		header: () => makeSortableHeader("processingFinishedAt", sortConfig, setSortConfig, "Processing Finished"),
        cell: (value, row) => <div className="dateTimeCell">{formatDateTime(row.processingFinishedAt) ?? "-"}</div>,
    };
}

export function metadataAddedByCol(
    sortConfig: SortConfig,
    setSortConfig: (val: SortConfig) => void,
    label: string
): SimpleColumn<VideoTable> {
    return {
		id: "approvedBy",
		key: "approvedBy",
		label,
		header: () => makeSortableHeader("metadataAddedBy", sortConfig, setSortConfig, label),
        cell: (value, row) => <div className="dateTimeCell wrapCell">{row.metadataAddedBy ?? "-"}</div>,
    };
}

export function sourceCol(
	sortConfig: SortConfig,
	setSortConfig: (val: SortConfig) => void,
): SimpleColumn<VideoTable> {
    return {
		id: "source",
		key: "origin",
		label: "Source",
		header: () => makeSortableHeader("origin", sortConfig, setSortConfig, "Source"),
        cell: (value, row) => <div className="dateTimeCell wrapCell">{formatSource(row.origin, row.provider) ?? "-"}</div>,
    };
}

export function deletedAtCol(
	sortConfig: SortConfig,
	setSortConfig: (val: SortConfig) => void,
): SimpleColumn<VideoTable> {
    return {
		id: "deletedAt",
		key: "deletedAt",
		label: "Deleted On",
		header: () => makeSortableHeader("deletedAt", sortConfig, setSortConfig, "Deleted On"),
        cell: (value, row) => <div className="dateTimeCell">{formatDateTime(row.deletedAt) ?? "-"}</div>,
    };
}

export function deletedByCol(
	sortConfig: SortConfig,
	setSortConfig: (val: SortConfig) => void,
): SimpleColumn<VideoTable> {
    return {
		id: "deletedBy",
		key: "deletedBy",
		label: "Deleted By",
		header: () => makeSortableHeader("deletedBy", sortConfig, setSortConfig, "Deleted By"),
        cell: (value, row) => <div className="dateTimeCell wrapCell">{row.deletedBy ?? "-"}</div>,
    };
}

export function veoClubhouseCol(
	sortConfig: SortConfig,
	setSortConfig: (val: SortConfig) => void,
): SimpleColumn<VideoTable> {
    return {
		id: "veoClubhouse",
		key: "providerMetadata",
		label: "Veo Clubhouse",
		header: () =>
			makeSortableHeader(
				VideoProviderMetadataFilterField.ClubhouseName,
				sortConfig,
				setSortConfig,
				"Veo Clubhouse",
			),
        cell: (value, row) => (
            <div className="dateTimeCell wrapCell">{getProviderMetadataField(row.providerMetadata, VideoProviderMetadataField.ClubhouseName) ?? "-"}</div>
        ),
    };
}

function unextractedCol(
	id: string,
	header: string,
	sortField: string,
	sortConfig: SortConfig,
	setSortConfig: (val: SortConfig) => void,
): SimpleColumn<VideoTable> {
	return {
		id,
		key: "providerMetadata",
		label: header,
		header: () => makeSortableHeader(sortField, sortConfig, setSortConfig, header),
        cell: () => <div className="dateTimeCell">-</div>,
    };
}

export function apiStatusCol(
	sortConfig: SortConfig,
	setSortConfig: (val: SortConfig) => void,
): SimpleColumn<VideoTable> {
    return {
		id: "apiStatus",
		key: "status",
		label: "API Status",
		header: () => makeSortableHeader("apiStatus", sortConfig, setSortConfig, "API Status"),
        cell: (value, row) => <div className="dateTimeCell wrapCell">{formatApiStatus(row.status) ?? "-"}</div>,
    };
}

export function statusCol(
    sortConfig: SortConfig,
    setSortConfig: (val: SortConfig) => void
): SimpleColumn<VideoTable> {
    return {
		id: "status",
		key: "status",
		label: "Status",
		header: () => makeSortableHeader("status", sortConfig, setSortConfig, "Status"),
        cell: (value, row) => (
            <div className={getStatusClass(row.status)}>
                {row.status?.replace(/_/g, " ") ?? "-"}
            </div>
        ),
    };
}

export function lastEventCol(
    sortConfig: SortConfig,
    setSortConfig: (val: SortConfig) => void
): SimpleColumn<VideoTable> {
    return {
		id: "lastEvent",
		key: "lastEvent",
		label: "Last Event",
		header: () => makeSortableHeader("lastEvent", sortConfig, setSortConfig, "Last Event"),
        cell: (value, row) => (
            <div className="eventCell">
                {row?.lastEvent?.replace(/_/g, " ") ?? "-"}
            </div>
        ),
    };
}

export function uploadedByCol(
    sortConfig: SortConfig,
    setSortConfig: (val: SortConfig) => void
): SimpleColumn<VideoTable> {
    return {
		id: "uploadedBy",
		key: "uploadedBy",
		label: "Uploaded By",
		header: () => makeSortableHeader("uploadedBy", sortConfig, setSortConfig, "Uploaded By"),
        cell: (value, row) => <div className="dateTimeCell wrapCell">{row.uploadedBy ?? "-"}</div>,
    };
}

export function defaultActionsCol(): SimpleColumn<VideoTable> {
	return {
		id: "actions",
		key: "actions",
		label: "Actions",
		header: () => "Actions",
		cell: (value, row) => (
			<div className="actionsCell">
				<RowActionLink icon={rowActionIcons.status} label="Status" to={`/video-uploads/${row.id}/status`} />
			</div>
		),
	};
}

interface GetColumnsArgs {
    preset: Preset;
    sortConfig: SortConfig;
    setSortConfig: (val: SortConfig) => void;
    actions?: SimpleColumn<VideoTable>;
}

function uploadColumns(args: GetColumnsArgs): SimpleColumn<VideoTable>[] {
    const { sortConfig, setSortConfig } = args;

    return [
        nameCol(sortConfig, setSortConfig),
        createdAtCol(sortConfig, setSortConfig),
        durationCol(sortConfig, setSortConfig),
        sourceCol(sortConfig, setSortConfig),
        apiStatusCol(sortConfig, setSortConfig),
        metadataCol(sortConfig, setSortConfig),
        sizeCol(sortConfig, setSortConfig),
        uploadedByCol(sortConfig, setSortConfig),
        metadataAddedByCol(sortConfig, setSortConfig, "Saved By"),
    ];
}

export function getColumns(args: GetColumnsArgs): SimpleColumn<VideoTable>[] {
    const { preset, sortConfig, setSortConfig, actions } = args;

    switch (preset) {
        case "waiting-for-approval":
        case "approved": {
            const cols = uploadColumns(args);

            if (actions) {
                cols.push(actions);
            } else {
                cols.push(defaultActionsCol());
            }
            return cols;
        }
        case "in-processing": {
            const cols: SimpleColumn<VideoTable>[] = [
                workerCol(sortConfig, setSortConfig),
                nameCol(sortConfig, setSortConfig),
                createdAtCol(sortConfig, setSortConfig),
                durationCol(sortConfig, setSortConfig),
                sourceCol(sortConfig, setSortConfig),
                sizeCol(sortConfig, setSortConfig),
                metadataCol(sortConfig, setSortConfig),
                processingStartedCol(sortConfig, setSortConfig),
            ];
            if (actions) {
                cols.push(actions);
            } else {
                cols.push(defaultActionsCol());
            }
            return cols;
        }
        case "errored": {
            const cols: SimpleColumn<VideoTable>[] = [
                nameCol(sortConfig, setSortConfig),
                createdAtCol(sortConfig, setSortConfig),
                durationCol(sortConfig, setSortConfig),
                sourceCol(sortConfig, setSortConfig),
                metadataCol(sortConfig, setSortConfig),
                statusCol(sortConfig, setSortConfig),
                lastEventCol(sortConfig, setSortConfig),
            ];
            if (actions) {
                cols.push(actions);
            } else {
                cols.push(defaultActionsCol());
            }
            return cols;
        }
        case "deleted": {
            const cols: SimpleColumn<VideoTable>[] = [
                nameCol(sortConfig, setSortConfig),
                createdAtCol(sortConfig, setSortConfig),
                durationCol(sortConfig, setSortConfig),
                deletedAtCol(sortConfig, setSortConfig),
                sourceCol(sortConfig, setSortConfig),
                sizeCol(sortConfig, setSortConfig),
                deletedByCol(sortConfig, setSortConfig),
            ];

            if (actions) {
                cols.push(actions);
            } else {
                cols.push(defaultActionsCol());
            }
            return cols;
        }
        case "finished": {
            const cols: SimpleColumn<VideoTable>[] = [
                nameCol(sortConfig, setSortConfig),
                createdAtCol(sortConfig, setSortConfig),
                durationCol(sortConfig, setSortConfig),
                sourceCol(sortConfig, setSortConfig),
                metadataCol(sortConfig, setSortConfig),
                processingStartedCol(sortConfig, setSortConfig),
                processingFinishedCol(sortConfig, setSortConfig),
            ];
            if (actions) {
                cols.push(actions);
            } else {
                cols.push(defaultActionsCol());
            }
            return cols;
        }
        default: {
            const cols: SimpleColumn<VideoTable>[] = [
                nameCol(sortConfig, setSortConfig),
                createdAtCol(sortConfig, setSortConfig),
                durationCol(sortConfig, setSortConfig),
                statusCol(sortConfig, setSortConfig),
                sourceCol(sortConfig, setSortConfig),
                apiStatusCol(sortConfig, setSortConfig),
                metadataCol(sortConfig, setSortConfig),
                sizeCol(sortConfig, setSortConfig),
                uploadedByCol(sortConfig, setSortConfig),
                metadataAddedByCol(sortConfig, setSortConfig, "Saved By"),
                unextractedCol(
                    "veoCameraId",
                    "Veo Camera ID",
                    VideoProviderMetadataFilterField.RecordingSlug,
                    sortConfig,
                    setSortConfig,
                ),
                veoClubhouseCol(sortConfig, setSortConfig),
                unextractedCol("veoTeam", "Veo Team", "veo_team", sortConfig, setSortConfig),
                lastEventCol(sortConfig, setSortConfig),
            ];
            if (actions) {
                cols.push(actions);
            } else {
                cols.push(defaultActionsCol());
            }
            return cols;
        }
    }
}
